// The current field as the current looks read it (R2716;
// ui-map-interface.md §17.3.07, v2-the-deep.md §5; mockup round 117).
//
// The wire sends one current a cell (`Hello.currents`, R2609). A streak
// that read the value of its cell would kink at every cell edge, so the
// field is resampled ONCE per field onto a square grid of `sample_px`
// world px: each node takes the mean of its cell and the six cells round
// it, weighted by a Gaussian of `smooth_px`. A point between four nodes
// reads them bilinearly, as the field of round 117 does. A node on rock
// is dry, and a point with two or more dry nodes round it reads no
// current, so a streak never crosses a ridge.
//
// The cold speed ramp of round 117 is here too: five stops of the speed
// against the strongest cell, in the inks deep, faint, dim, pale and the
// bright accent of the palette, the alpha rising with the speed. Every
// figure is the pack's (`[chart.piece.current_arrows]`).
//
// Everything here is pure, so a test reads it with no engine.

import { BLOCK_RIDGE, COL_PITCH, HEX_SIZE } from '../../../gen/balance';
import { CHART } from '../../../gen/presentation';
import { cellAt, cellCenterX, cellCenterY, neighborAt, worldSize } from '../../../world';
import { mixColor } from '../../../render/color';
import { CHART_INK } from './shared';

/** The figures of the piece's own table. */
const T = CHART.pieces.current_arrows;

/** The current field on a square grid, in px a map hour at tide factor 1. */
export interface FlowGrid {
  /** The node step in world px. */
  step: number;
  nx: number;
  ny: number;
  vx: Float32Array;
  vy: Float32Array;
  /** 1 where the node stands on water. */
  wet: Uint8Array;
}

/**
 * The node count of the grid on each axis for a world of `cols` × `rows`
 * cells: one node a `stepPx` from the origin, past the far edge. The
 * engine sizes the speed plane by it (R2827), so the plane and the grid
 * are one shape.
 */
export function flowGridSize(
  cols: number, rows: number, stepPx: number = T.fig.sample_px,
): { nx: number; ny: number } {
  const step = Math.max(1, stepPx);
  const { w, h } = worldSize(cols, rows);
  return { nx: Math.ceil(w / step) + 1, ny: Math.ceil(h / step) + 1 };
}

/**
 * Resamples a cell field onto the grid. `field` holds the int8 pair of each
 * cell, `unitPxH` the px a map hour of one unit, and `blocked` the blocked
 * mask of the hello (a ridge cell is dry), or null.
 */
export function buildFlowGrid(
  field: Int8Array, unitPxH: number, cols: number, rows: number,
  blocked: Uint8Array | null,
  stepPx = T.fig.sample_px, smoothPx = T.fig.smooth_px,
): FlowGrid {
  const step = Math.max(1, stepPx);
  const { nx, ny } = flowGridSize(cols, rows, step);
  const g: FlowGrid = {
    step, nx, ny, vx: new Float32Array(nx * ny), vy: new Float32Array(nx * ny), wet: new Uint8Array(nx * ny),
  };
  const s2 = Math.max(1e-6, smoothPx * smoothPx);
  for (let j = 0; j < ny; j++) {
    for (let i = 0; i < nx; i++) sampleNode(g, field, unitPxH, cols, rows, blocked, s2, i, j);
  }
  return g;
}

/** Is cell `c` dry: off the grid, or a ridge of the blocked mask? */
function dryCell(c: number, blocked: Uint8Array | null): boolean {
  return c < 0 || (blocked !== null && blocked[c] === BLOCK_RIDGE);
}

/**
 * Samples node (i, j) of the grid: the mean of its cell and the six cells
 * round it, weighted by a Gaussian of the square root of `s2`. The full
 * build and the patch both call it, so a patched node is the node of a
 * full build, bit for bit (R2897).
 */
function sampleNode(
  g: FlowGrid, field: Int8Array, unitPxH: number, cols: number, rows: number,
  blocked: Uint8Array | null, s2: number, i: number, j: number,
): void {
  const x = i * g.step, y = j * g.step;
  const c = cellAt(x, y, cols, rows);
  const at = j * g.nx + i;
  g.vx[at] = 0;
  g.vy[at] = 0;
  g.wet[at] = 0;
  if (dryCell(c, blocked)) return;
  let sx = 0, sy = 0, sw = 0;
  for (let k = -1; k < 6; k++) {
    const n = k < 0 ? c : neighborAt(c, k, cols, rows);
    if (dryCell(n, blocked) || 2 * n + 1 >= field.length) continue;
    const dx = cellCenterX(n, cols) - x, dy = cellCenterY(n, cols) - y;
    const wgt = Math.exp(-(dx * dx + dy * dy) / s2);
    sx += field[2 * n] * wgt;
    sy += field[2 * n + 1] * wgt;
    sw += wgt;
  }
  g.wet[at] = 1;
  if (sw > 0) { g.vx[at] = (sx / sw) * unitPxH; g.vy[at] = (sy / sw) * unitPxH; }
}

/**
 * How far from the centre of a cell a node can stand and still read the
 * cell, in world px: the next cell centre is one column pitch away, and a
 * node reads the cell it stands in, at most one hex radius from its
 * centre. One px covers the rounding.
 */
const PATCH_REACH_PX = COL_PITCH + HEX_SIZE + 1;

/**
 * Resamples a grid in place round the cells a survey pass wrote (R2897;
 * ui-map-interface.md §17.3.07). A node reads its own cell and the six
 * round it, so a written cell moves the nodes within `PATCH_REACH_PX` of
 * its centre and no other. `begin` takes the field once for a pass, `cell`
 * resamples the nodes round one cell. The nodes it sampled are left in
 * `list`, and their rect in `i0` to `j1`, so the speed field writes those
 * nodes and uploads those rows alone. Each node is sampled once a pass.
 * The stamp plane and the list are the one allocation, made once for a
 * grid size, so a pass allocates nothing (§10).
 */
export class FlowGridPatch {
  /** The node rect the pass moved, inclusive; empty while `i1 < i0`. */
  i0 = 0;
  j0 = 0;
  i1 = -1;
  j1 = -1;
  /** How many nodes the pass sampled: the head of `list`. */
  nodes = 0;
  /** The index of each node the pass sampled, in sample order. */
  list = new Int32Array(0);
  /** `cell` as a function made once, for `fieldCellsSince`. */
  readonly visit = (c: number): void => { this.cell(c); };

  private stamp = new Uint32Array(0);
  private gen = 0;
  private g: FlowGrid | null = null;
  private field: Int8Array = new Int8Array(0);
  private unit = 0;
  private cols = 0;
  private rows = 0;
  private blocked: Uint8Array | null = null;
  private s2 = 1;

  /** Opens a pass over the grid `g` of the field. */
  begin(
    g: FlowGrid, field: Int8Array, unitPxH: number, cols: number, rows: number,
    blocked: Uint8Array | null, smoothPx = T.fig.smooth_px,
  ): void {
    const n = g.nx * g.ny;
    if (this.stamp.length !== n) { this.stamp = new Uint32Array(n); this.list = new Int32Array(n); this.gen = 0; }
    if (this.gen >= 0xfffffffe) { this.stamp.fill(0); this.gen = 0; }
    this.gen++;
    this.g = g;
    this.field = field;
    this.unit = unitPxH;
    this.cols = cols;
    this.rows = rows;
    this.blocked = blocked;
    this.s2 = Math.max(1e-6, smoothPx * smoothPx);
    this.i0 = g.nx;
    this.j0 = g.ny;
    this.i1 = -1;
    this.j1 = -1;
    this.nodes = 0;
  }

  /** Resamples the nodes round cell `c`. A cell off the grid moves none. */
  cell(c: number): void {
    const g = this.g;
    if (!g || c < 0 || c >= this.cols * this.rows) return;
    const cx = cellCenterX(c, this.cols), cy = cellCenterY(c, this.cols);
    const i0 = Math.max(0, Math.floor((cx - PATCH_REACH_PX) / g.step));
    const i1 = Math.min(g.nx - 1, Math.ceil((cx + PATCH_REACH_PX) / g.step));
    const j0 = Math.max(0, Math.floor((cy - PATCH_REACH_PX) / g.step));
    const j1 = Math.min(g.ny - 1, Math.ceil((cy + PATCH_REACH_PX) / g.step));
    if (i1 < i0 || j1 < j0) return;
    const stamp = this.stamp, gen = this.gen;
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const at = j * g.nx + i;
        if (stamp[at] === gen) continue;
        stamp[at] = gen;
        sampleNode(g, this.field, this.unit, this.cols, this.rows, this.blocked, this.s2, i, j);
        this.list[this.nodes++] = at;
      }
    }
    if (i0 < this.i0) this.i0 = i0;
    if (j0 < this.j0) this.j0 = j0;
    if (i1 > this.i1) this.i1 = i1;
    if (j1 > this.j1) this.j1 = j1;
  }
}

/**
 * The current at a world point, bilinear between the four nodes round it,
 * into `out` as [vx, vy] in px a map hour at tide factor 1. False, and 0,
 * off the grid or where two or more of the four nodes are dry.
 */
export function flowAt(g: FlowGrid, x: number, y: number, out: [number, number]): boolean {
  const fx = x / g.step, fy = y / g.step;
  const i = Math.floor(fx), j = Math.floor(fy);
  if (!(i >= 0 && j >= 0 && i < g.nx - 1 && j < g.ny - 1)) { out[0] = 0; out[1] = 0; return false; }
  const k = j * g.nx + i;
  const w = g.wet;
  if (w[k] + w[k + 1] + w[k + g.nx] + w[k + g.nx + 1] <= 2) { out[0] = 0; out[1] = 0; return false; }
  const ax = fx - i, ay = fy - j;
  const w00 = (1 - ax) * (1 - ay), w10 = ax * (1 - ay), w01 = (1 - ax) * ay, w11 = ax * ay;
  out[0] = g.vx[k] * w00 + g.vx[k + 1] * w10 + g.vx[k + g.nx] * w01 + g.vx[k + g.nx + 1] * w11;
  out[1] = g.vy[k] * w00 + g.vy[k + 1] * w10 + g.vy[k + g.nx] * w01 + g.vy[k + g.nx + 1] * w11;
  return true;
}

/** The five inks of the cold ramp, in stop order: no colour is new. */
export const RAMP_INKS: readonly number[] = [
  CHART_INK.deep, CHART_INK.faint, CHART_INK.dim, CHART_INK.pale, CHART_INK.bright,
];

/** One colour and one alpha of the ramp. */
export interface RampPick { ink: number; alpha: number }

/**
 * The ramp at the speed share `u` (0 slack, 1 the strongest cell), into
 * `out`: the ink and the alpha of the two stops round it, mixed.
 */
export function rampAt(u: number, out: RampPick): RampPick {
  const at = T.arr.ramp_at, al = T.arr.ramp_alpha;
  const v = Math.max(0, Math.min(1, u));
  const n = Math.min(at.length, al.length, RAMP_INKS.length);
  out.ink = RAMP_INKS[0];
  out.alpha = al[0] ?? 0;
  for (let i = 1; i < n; i++) {
    if (v > at[i]) continue;
    const q = (v - at[i - 1]) / Math.max(1e-9, at[i] - at[i - 1]);
    out.ink = mixColor(RAMP_INKS[i - 1], RAMP_INKS[i], q);
    out.alpha = al[i - 1] + (al[i] - al[i - 1]) * q;
    return out;
  }
  out.ink = RAMP_INKS[n - 1];
  out.alpha = al[n - 1] ?? 0;
  return out;
}

/** The Hermite step of the cards: 0 under `e0`, 1 over `e1`. */
export function smooth(e0: number, e1: number, v: number): number {
  const q = Math.max(0, Math.min(1, (v - e0) / Math.max(1e-9, e1 - e0)));
  return q * q * (3 - 2 * q);
}

/**
 * The lattice stride of a mark that keeps at least `gapPx` screen px
 * between two marks at the zoom `zoom`: 1, 2, 4 ... cells, so the count a
 * screen holds stays near one figure at every zoom.
 */
export function strideFor(gapPx: number, zoom: number): number {
  const cells = gapPx / Math.max(1e-6, COL_PITCH * zoom);
  return cells <= 1 ? 1 : 2 ** Math.ceil(Math.log2(cells));
}
